'use client';

import DescriptionIcon from '@mui/icons-material/Description';
import { Stack, Theme, Typography } from '@mui/material';
import { withClarityTheme } from '@verifime/styles';
import { ReactElement, ReactNode } from 'react';
import { allGroupsHiddenOnMobile } from './allGroupsHiddenOnMobile';

export type IdentityRequirementItem = {
  /** Stable key for React. Not rendered. */
  key: string;
  /** The item's label. Customer-facing copy, so the caller owns it. */
  label: ReactNode;
  /** Leading glyph. Defaults to a document icon when omitted. */
  icon?: ReactNode;
};

export type IdentityRequirementGroup = {
  key: string;
  /** Heading for this group. Caller owns the wording. */
  label: ReactNode;
  /** How many of the items are needed, in the caller's words. Omitted when every
   *  item is required. */
  rule?: ReactNode;
  items: ReadonlyArray<IdentityRequirementItem>;
  /** Trailing note, for example a substitution that applies to some customers.
   *  Rendered only when supplied, so it is never standing text. */
  note?: ReactNode;
  /** Rendered inside this group's own card, after its items (and after `note`, if supplied).
   *  Never standing content: omitted, like `note`, whenever a caller has nothing to put there.
   *
   *  This is a per-group slot, not a component-level one, and that is a reversal: an earlier
   *  version of this screen composed its DesktopQrReveal outside IdentityRequirementSummary
   *  entirely, as a sibling in the page assembling it, because this type carried no trailing slot
   *  of its own. That put a card's own content outside its card. The design board never separated
   *  them - `.qr` is a direct child of `.reqcard`, a sibling of the label and the item row, not of
   *  the whole panel - so this field puts the slot back where the board always had it. A
   *  component-level slot (rendered once, after the last card) was considered and rejected: only
   *  the webcam group needs this, and a component-level slot would reproduce the same
   *  "outside the right card" defect for any group that is not last. */
  trailingSlot?: ReactNode;
  /** Hidden below the first breakpoint. Set this on a group that is self evident
   *  on a phone, such as needing a camera. */
  hiddenOnMobile?: boolean;
};

export type IdentityRequirementSummaryProps = {
  /** Heading for the whole block. Caller owns the wording. */
  heading: ReactNode;
  groups: ReadonlyArray<IdentityRequirementGroup>;
  'data-testid'?: string;
};

/**
 * The magic-link landing screen's "what you will need" block: a brand-washed panel
 * holding one card per requirement group, for example a photo ID and a camera for
 * the face check.
 *
 * ADR-0050: every visible string, and even the item and group ordering, arrives as
 * a prop. This component never calls the API and never authors copy.
 *
 * Wrapped in withClarityTheme: `theme.palette.text.tertiary` (both the group rule and the note)
 * does not exist at all on the default theme, so a caller outside a Clarity tree silently got the
 * wrong-weight text rather than the muted tone. (`surface.brandSubtle`, the panel's own
 * background, is not the reason: it resolves to the identical hex on both theme families, since
 * both derive it from the same Eggplant primitive ramp.) Nesting withClarityTheme inside an
 * already Clarity-themed screen is idempotent (the HOC re-derives the same Clarity theme from the
 * ambient light/dark mode), so this costs nothing for today's callers.
 *
 * The group rule renders `text.tertiary`, not the board's own Grey 700 value: a design ruling
 * removed the dedicated `text.supporting` palette member that used to carry that exact
 * light-mode value, and this consumer moved back onto `text.tertiary` (Grey 500 in light).
 * Dark mode is unaffected, since both tokens always resolved to the same Grey 300 there. The
 * light-mode deviation from the board is recorded as TD-055 in docs/technical-debt.md.
 */
function IdentityRequirementSummaryComponent({
  heading,
  groups,
  'data-testid': dataTestId,
}: Readonly<IdentityRequirementSummaryProps>): ReactElement {
  // When every group is hidden below the mobile breakpoint (for example a screen whose sole
  // requirement is a camera, self-evident on a phone), the panel has nothing left to show
  // there: without this, the heading below still rendered over an empty brand-washed box.
  // Computed from `groups`, the same responsive-array form each card already uses for its own
  // `hiddenOnMobile`, rather than a viewport hook: the state is fully known from props.
  const hiddenOnMobile = allGroupsHiddenOnMobile(groups);

  return (
    <Stack
      data-testid={dataTestId}
      sx={(theme: Theme) => ({
        display: hiddenOnMobile ? ['none', 'flex'] : 'flex',
        boxSizing: 'border-box',
        // The brand wash is a light-mode treatment only: the artboard's dark rule puts this
        // panel on the neutral subtle surface instead, with a subtle border, so the panel does
        // not read as a purple-tinted near-black. No single token expresses "brand in light,
        // neutral in dark" - surface.brandSubtle recalibrates per mode but stays eggplant in
        // both - so this branches on palette.mode explicitly.
        //
        // Branching on palette.mode inside a component is a smell, and worth saying so
        // plainly: the right home for this is a contextual Tier 3 token that encodes the
        // intent once ("this panel's mode-dependent surface"), so every future component
        // wanting the same treatment does not have to repeat this branch. See TD-035.
        backgroundColor:
          theme.palette.mode === 'dark'
            ? theme.palette.surface.neutralSubtle
            : theme.palette.surface.brandSubtle,
        border: theme.palette.mode === 'dark' ? `1px solid ${theme.palette.divider}` : 'none',
        borderRadius: theme.semantic.borderRadius.lg,
        padding: theme.semantic.spacing.base,
        gap: theme.semantic.spacing.sm,
      })}
    >
      <Typography variant="h6">{heading}</Typography>
      {groups.map((group) => (
        <Stack
          key={group.key}
          sx={(theme: Theme) => ({
            // Hidden below the first breakpoint via a responsive `display` value: at
            // mobile width this resolves to `display: none`, which every mainstream
            // screen reader excludes from the accessibility tree (confirmed at
            // 375px: offsetParent is null and the subtree is out of the tree), which
            // is exactly what "hidden on mobile" requires. At wider viewports it
            // resolves to `flex` and the card renders normally.
            display: group.hiddenOnMobile ? ['none', 'flex'] : 'flex',
            // The panel this card sits in may inherit a centred `textAlign` from an
            // ancestor screen (see the magic-link continue screen); a centred list
            // of requirements is unreadable, so every card resets to the left.
            textAlign: 'left',
            gap: theme.semantic.spacing.xs,
            padding: theme.semantic.spacing.base,
            borderRadius: theme.semantic.borderRadius.sm,
            border: `1px solid ${theme.palette.border.subtleAccent}`,
            backgroundColor: theme.palette.background.paper,
          })}
        >
          {/* The board specifies `gap: 10px` on this column (board.css:76), and the
              Clarity spacing scale cannot express 10: it runs `4xs` 1, `3xs` 2, `2xs` 4,
              `xs` 8, `sm` 12. This uses `xs` (8px) anyway, accepting a 2px deviation from
              the board, because staying on the token scale is worth more than the 2px. The
              value gap is recorded as TD-053 in the technical-debt register; that row lands
              on a separate branch and may not yet be present in this branch's
              docs/technical-debt.md. */}
          <Stack sx={(theme: Theme) => ({ gap: theme.semantic.spacing.xs })}>
            {/* component="p": a group label names the list beneath it, it is not a
                heading in its own right. Left as the subtitle1 variant's default
                `<h6>`, every group label would be a sibling heading next to the
                block's one real heading, with no level between them. The block
                heading (above, `variant="h6"`) keeps the MUI default: it is the
                one heading in this block, and the component cannot know what
                level the surrounding page is already on. */}
            <Typography variant="subtitle1" component="p">
              {group.label}
            </Typography>
            {group.rule && (
              <Typography variant="body2" color="text.tertiary">
                {group.rule}
              </Typography>
            )}
          </Stack>

          {/* A real list (ADR-0072), not a div stack: a screen reader announces
              "list, N items" and offers list navigation over the accepted
              documents. `listStyle`/`margin`/`padding` strip the default bullet
              and indent that a plain `<ul>` would otherwise draw. */}
          <Stack
            component="ul"
            // Safari and VoiceOver strip a `<ul>`'s implicit list role when its
            // `list-style` is `none` (a documented WebKit behaviour), so this
            // `role="list"` restores the role the browser removes rather than
            // duplicating one that is already there. That is not the pattern
            // ADR-0072 forbids: that ADR forbids a custom attribute that duplicates
            // semantics an element already exposes natively. This is the opposite
            // case: it restores a role WebKit actively strips, so without it there
            // is no native list semantic left for Safari/VoiceOver to fall back on.
            // This screen is reached from an email on a phone, so iOS VoiceOver is
            // a primary case, not an edge one. Do not delete this as redundant with
            // `component="ul"`: without it, Safari/VoiceOver users lose the list
            // announcement this fix exists for. The `<li>` rows need no equivalent
            // restoration.
            role="list"
            sx={(theme: Theme) => ({
              gap: theme.semantic.spacing.xs,
              listStyle: 'none',
              margin: 0,
              padding: 0,
            })}
          >
            {group.items.map((item) => (
              <Stack
                key={item.key}
                component="li"
                direction="row"
                sx={(theme: Theme) => ({
                  alignItems: 'center',
                  gap: theme.semantic.spacing.xs,
                })}
              >
                {item.icon ?? (
                  <DescriptionIcon
                    aria-hidden
                    sx={(theme: Theme) => ({
                      color: theme.palette.primary.main,
                      // '20px' is a deliberate hardcode, not a missed token. The design's icon
                      // box is 20 CSS px (index.html:171, `<vm-icon name="DescriptionFilled"
                      // size="20">`, mapped straight onto the svg's width and height). The
                      // Clarity size scale has no step between `8xs` (16px) and `7xs` (24px):
                      // `7xs` was nearer, not right, and painted a 24px box that made requirement
                      // rows measure 40 device px tall against the design's 34. A token set that
                      // cannot express the design is a gap in the token set, not a licence to
                      // move the design. `8xs` (16px) was then tested against the board rather
                      // than reasoned about, and is measurably wrong: rendering this glyph
                      // through the same rasteriser as the reference, the painted box is 22x26
                      // device px at a 16px font size and 26x34 at 20px, while all three
                      // reference rows measure 26x34. 20px reproduces the board exactly, down to
                      // the painted pixel count. Both reference generations agree on this (the
                      // 780x1682 reissue and the earlier 780x1698), so the result does not depend
                      // on which one you diff against. MUI's SvgIcon sets `width: 1em; height: 1em` on its
                      // root, driven by `fontSize`, which is why `fontSize` is the lever here
                      // rather than `width`/`height` directly. This value is coupled to the
                      // camera icon's own fontSize in buildIdentityRequirementGroups.tsx (see
                      // that file's own comment): both draw the same glyph at the same box, and
                      // changing one without the other is a defect.
                      fontSize: '20px',
                    })}
                  />
                )}
                <Typography variant="body2">{item.label}</Typography>
              </Stack>
            ))}
          </Stack>

          {group.note && (
            <Typography variant="caption" color="text.tertiary">
              {group.note}
            </Typography>
          )}

          {group.trailingSlot}
        </Stack>
      ))}
    </Stack>
  );
}

export const IdentityRequirementSummary = withClarityTheme(IdentityRequirementSummaryComponent);
